import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getUserApplications, 
  updateReferral, 
  getReferrerSuggestions,
  UserApplication,
  ReferrerSuggestion 
} from '../../api/referralServices';
import './MyApplicationsReferral.css';
import tiktokIcon from '../../assets/icons/tiktok-icon.svg';

// Update the interface to match the actual API response
interface ApiApplication {
  id: number;
  collab: {
    id: number;
    name: string;
    description: string;
    status: string;
  };
  applied_at: string;
  referrer: {
    id: number;
    email: string;
    username: string;
  } | null;
  has_referral: boolean;
}

// Interface for the API response structure
interface ApiResponse {
  applications: ApiApplication[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_count: number;
    page_size: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

const MyApplicationsReferral: React.FC = () => {
  const [applications, setApplications] = useState<UserApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [referralInputs, setReferralInputs] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState<Record<number, boolean>>({});
  const [success, setSuccess] = useState<Record<number, boolean>>({});
  const [inputErrors, setInputErrors] = useState<Record<number, string>>({});
  const [suggestions, setSuggestions] = useState<Record<number, ReferrerSuggestion[]>>({});
  const [activeAppId, setActiveAppId] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const autocompleteRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const [validSelections, setValidSelections] = useState<Record<number, boolean>>({});

  // Fetch user's applications
  useEffect(() => {
    const fetchApplications = async () => {
      setLoading(true);
      try {
        const apiResponse = await getUserApplications();
        console.log('API Response:', apiResponse);
        
        // Access the applications array from the response data
        const apiData = apiResponse as unknown as ApiResponse;
        
        // Transform the API response format to match what the component expects
        const transformedApps = Array.isArray(apiData.applications) 
          ? apiData.applications.map((app: ApiApplication) => ({
              application_id: app.id,
              collab_id: app.collab.id,
              brand_name: '', // This might need to come from elsewhere
              collab_name: app.collab.name,
              application_date: app.applied_at,
              status: app.collab.status,
              referrer_username: app.referrer?.username || null
            }))
          : [];
        
        setApplications(transformedApps);
      } catch (error) {
        console.error('Error fetching applications:', error);
        setError('Failed to load your applications. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, []);

  // Effect to handle clicks outside of autocomplete dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
        setActiveAppId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = async (applicationId: number, value: string) => {
    // If this was a valid selection but user is typing again, invalidate it
    if (validSelections[applicationId]) {
      setValidSelections(prev => ({
        ...prev,
        [applicationId]: false
      }));
    }
    
    setReferralInputs(prev => ({ ...prev, [applicationId]: value }));
    
    // Clear any previous errors for this application
    if (inputErrors[applicationId]) {
      setInputErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[applicationId];
        return newErrors;
      });
    }

    // Fetch suggestions if input has at least 2 characters
    if (value.length >= 2) {
      try {
        const suggestionResults = await getReferrerSuggestions(value);
        setSuggestions(prev => ({ ...prev, [applicationId]: suggestionResults }));
        setActiveAppId(applicationId);
        setFocusedIndex(-1);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    } else {
      // Clear suggestions if input is too short
      setSuggestions(prev => {
        const newSuggestions = { ...prev };
        delete newSuggestions[applicationId];
        return newSuggestions;
      });
      setActiveAppId(null);
    }
  };

  const handleSuggestionClick = (applicationId: number, suggestion: ReferrerSuggestion) => {
    // Update the input field with the selected username
    const username = suggestion.username;
    console.log('Setting selected username:', username, 'for application:', applicationId);
    
    // Set the input value and mark it as a valid selection
    setReferralInputs(prev => {
      const updated = { ...prev, [applicationId]: username };
      console.log('Updated referralInputs:', updated);
      return updated;
    });
    
    // Mark this as a valid selection
    setValidSelections(prev => ({
      ...prev,
      [applicationId]: true
    }));
    
    // Hide the suggestions
    setSuggestions(prev => {
      const newSuggestions = { ...prev };
      delete newSuggestions[applicationId];
      return newSuggestions;
    });
    setActiveAppId(null);
  };

  const handleInputFocus = (applicationId: number) => {
    const value = referralInputs[applicationId];
    if (value && value.length >= 2) {
      setActiveAppId(applicationId);
    }
  };

  const handleKeyDown = (applicationId: number, e: React.KeyboardEvent) => {
    const suggestionsList = suggestions[applicationId] || [];
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => (prev < suggestionsList.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter' && focusedIndex >= 0 && suggestionsList[focusedIndex]) {
      e.preventDefault();
      console.log('Selecting via keyboard:', suggestionsList[focusedIndex]);
      handleSuggestionClick(applicationId, suggestionsList[focusedIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setActiveAppId(null);
    }
  };

  const validateUsername = (username: string): boolean => {
    // Basic validation - non-empty and no spaces
    return username.trim().length > 0 && !username.includes(' ');
  };

  const handleSubmit = async (applicationId: number) => {
    const referrerUsername = referralInputs[applicationId];
    
    // Only allow submission if a valid selection was made from autocomplete
    if (!validSelections[applicationId]) {
      setInputErrors(prev => ({ 
        ...prev, 
        [applicationId]: 'Please select a referrer from the dropdown' 
      }));
      return;
    }

    setSubmitting(prev => ({ ...prev, [applicationId]: true }));
    
    try {
      const response = await updateReferral(applicationId, referrerUsername, 'add');

      if (response.success) {
        // Show success message
        setSuccess(prev => ({ ...prev, [applicationId]: true }));
        
        // Update the application in our list
        setApplications(prevApps => 
          prevApps.map(app => 
            app.application_id === applicationId 
              ? { ...app, referrer_username: referrerUsername } 
              : app
          )
        );

        // Clear the input and valid selection state
        setReferralInputs(prev => {
          const newInputs = { ...prev };
          delete newInputs[applicationId];
          return newInputs;
        });
        
        setValidSelections(prev => {
          const newSelections = { ...prev };
          delete newSelections[applicationId];
          return newSelections;
        });
        
        // Hide success message after 3 seconds
        setTimeout(() => {
          setSuccess(prev => {
            const newSuccess = { ...prev };
            delete newSuccess[applicationId];
            return newSuccess;
          });
        }, 3000);
      }
    } catch (error: any) {
      console.error('Error submitting referral:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit referral';
      setInputErrors(prev => ({ ...prev, [applicationId]: errorMessage }));
    } finally {
      setSubmitting(prev => {
        const newSubmitting = { ...prev };
        delete newSubmitting[applicationId];
        return newSubmitting;
      });
    }
  };

  const handleClearReferral = async (applicationId: number) => {
    setSubmitting(prev => ({ ...prev, [applicationId]: true }));
    
    try {
      // Note: When removing a referral, the email is not required
      const response = await updateReferral(applicationId, '', 'remove');

      if (response.success) {
        // Update the application in our list
        setApplications(prevApps => 
          prevApps.map(app => 
            app.application_id === applicationId 
              ? { ...app, referrer_username: null } 
              : app
          )
        );
      }
    } catch (error) {
      console.error('Error clearing referral:', error);
      setInputErrors(prev => ({ 
        ...prev, 
        [applicationId]: 'Failed to clear referral' 
      }));
    } finally {
      setSubmitting(prev => {
        const newSubmitting = { ...prev };
        delete newSubmitting[applicationId];
        return newSubmitting;
      });
    }
  };

  if (loading) {
    return <div className="referral-container loading">Loading your applications...</div>;
  }

  if (error) {
    return (
      <div className="referral-container error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className="referral-container empty">
        <h2>No Applications Found</h2>
        <p>You haven't applied to any collabs yet. When you do, you can come back here to add referral information.</p>
        <button onClick={() => navigate('/playground')}>Browse Collabs</button>
      </div>
    );
  }

  return (
    <div className="referral-container">
      <div className="referral-header">
        <h1>My Applications</h1>
        <p>
          Add who referred you to these collabs. This helps us reward besties who bring new members to our community!
        </p>
      </div>

      <div className="applications-list">
        {Array.isArray(applications) && applications.map(application => (
          <div key={application.application_id} className="application-card">
            <div className="application-header">
              <h2>{application.collab_name}</h2>
              <span className={`status-badge status-${application.status.toLowerCase()}`} title="This indicates the collab's current status">
                {application.status}
              </span>
            </div>
            
            <div className="application-info">
              <p>Applied: {new Date(application.application_date).toLocaleDateString()}</p>
              <p className="status-info">Status: <span className="status-highlight">{application.status}</span> (collabs remain open until the brand selects creators)</p>
            </div>
            
            <div className="referral-section">
              <h3>Referrer Information</h3>
              
              {application.referrer_username ? (
                <div className="existing-referral">
                  <p>Referred by: <strong>{application.referrer_username}</strong></p>
                  <button 
                    className="change-button"
                    onClick={() => handleClearReferral(application.application_id)}
                    disabled={submitting[application.application_id]}
                  >
                    {submitting[application.application_id] ? 'Changing...' : 'Change'}
                  </button>
                </div>
              ) : (
                <div className="add-referral">
                  <label htmlFor={`referrer-${application.application_id}`}>
                    Who referred you to this collab? (Enter their Vivid username)
                  </label>
                  <div 
                    className="autocomplete-container" 
                    ref={autocompleteRef}
                    key={`autocomplete-${application.application_id}-${validSelections[application.application_id] ? 'selected' : 'unselected'}`}
                  >
                    {/* Debug info for development purposes */}
                    {/* <pre>
                      {JSON.stringify({ 
                        inputValue: referralInputs[application.application_id] || '',
                        isValid: validSelections[application.application_id],
                        appId: application.application_id
                      }, null, 2)}
                    </pre> */}
                    <input
                      id={`referrer-${application.application_id}`}
                      type="text"
                      placeholder="Search Referrer"
                      value={referralInputs[application.application_id] || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        handleInputChange(application.application_id, newValue);
                      }}
                      onFocus={() => handleInputFocus(application.application_id)}
                      onKeyDown={(e) => handleKeyDown(application.application_id, e)}
                      disabled={submitting[application.application_id]}
                      className={`referrer-input ${validSelections[application.application_id] ? 'referrer-input-selected' : ''}`}
                      autoComplete="off"
                    />
                    
                    {activeAppId === application.application_id && suggestions[application.application_id]?.length > 0 && (
                      <div className="autocomplete-results">
                        {suggestions[application.application_id].map((suggestion, index) => (
                          <div 
                            key={suggestion.user_id}
                            className={`autocomplete-item ${focusedIndex === index ? 'autocomplete-item-active' : ''}`}
                            onClick={() => handleSuggestionClick(application.application_id, suggestion)}
                          >
                            <div className="autocomplete-username">
                              <span className="autocomplete-profile-dot"></span>
                              <span className="autocomplete-name">{suggestion.username}</span>
                            </div>
                            
                            {suggestion.tiktok_username && (
                              <div className="autocomplete-tiktok">
                                @{suggestion.tiktok_username}
                                <img src={tiktokIcon} alt="TikTok" className="tiktok-icon" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {validSelections[application.application_id] && (
                      <div className="selected-indicator">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6.00016 10.7799L3.22016 7.9999L2.27349 8.9399L6.00016 12.6666L14.0002 4.66656L13.0602 3.72656L6.00016 10.7799Z" fill="#28a745"/>
                        </svg>
                        Selected from autocomplete
                      </div>
                    )}
                  </div>
                  
                  <button
                    className="submit-button"
                    onClick={() => handleSubmit(application.application_id)}
                    disabled={!validSelections[application.application_id] || submitting[application.application_id]}
                  >
                    {submitting[application.application_id] ? 'Submitting...' : 'Submit'}
                  </button>
                  
                  {inputErrors[application.application_id] && (
                    <p className="error-message">{inputErrors[application.application_id]}</p>
                  )}
                  {success[application.application_id] && (
                    <p className="success-message">Referral added successfully!</p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyApplicationsReferral; 